import React, { useState, useEffect } from 'react';
import * as yup from 'yup';

import BaseModal from '../../../components/BaseModal/BaseModal';
import Forms from '../../../components/Forms/Forms';
import { validateToSchema } from '../../../utils/validationUtils';
import { toasterNotify } from '../../../utils/toaster';
import { postPortOfEntry, putPortOfEntry } from '../../../services/portsOfEntryService';
import { isEmptyObject } from '../../../utils/dataUtils';

const NAME_MAX_LEN = 75;
const CODE_MAX_LEN = 10;

interface FormValues {
    port_of_entry_code?: string;
    port_of_entry_name?: string;
    status?: boolean;
}

export const baseSchema = yup.object({
	port_of_entry_code: yup.string().transform((value) => String(value).toUpperCase()).trim().default('').required('Code is required'),
	port_of_entry_name: yup.string().transform((value) => String(value).toUpperCase()).trim().default('').required('Name is required'),
	status: yup.string().transform((value) => String(value).toUpperCase()).trim().default('').required('Port of Entry status must be either ACTIVE or INACTIVE'),
});

const SavePortOfEntryModal = ({
	show,
	onCompletion,
	onClose,
	editPermission,
	portOfEntry,
}: {
	show: boolean;
	onCompletion: () => void;
	onClose: () => void;
	editPermission: boolean;
	portOfEntry?: Record<string, any>;
}) => {
	const [formValues, setFormValues] = useState<FormValues>({});
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
	const codecanBeEdited:boolean = editPermission && (portOfEntry === undefined);

	const validationErrors = validateToSchema(baseSchema, formValues);
	const code_pattern = new RegExp(/[^-,()A-Z]*/g);
	const name_pattern = new RegExp(/[^-,()A-Z0-9\s]*/g);
	useEffect(() => {
		if (show) {
			setFormValues(portOfEntry ? { port_of_entry_code: portOfEntry.port_of_entry_code, port_of_entry_name: portOfEntry.port_of_entry_name, status: portOfEntry.status === 'ACTIVE' ? true : false} : { port_of_entry_name:'', port_of_entry_code:'', status: true});
			setIsLoading(false);
			setIsSubmitting(false);
			setShowAllErrors(false);
		}
	}, [show]);

	const submitForm = async () => {
		if (isSubmitting) {
			return;
		}
		if (!isEmptyObject(validationErrors)) {
			setShowAllErrors(true);
			return;
		}

		const castedFormValues = {
			...baseSchema.cast(formValues, { stripUnknown: true }),
			status: formValues && formValues['status'] ? 'ACTIVE' : 'INACTIVE',
		};
		setIsSubmitting(true);

		try {
			if (portOfEntry) {
				await putPortOfEntry(portOfEntry?.port_of_entry_code, castedFormValues);
			} else {
				await postPortOfEntry(castedFormValues);
			}
			onCompletion();
			onClose();
			toasterNotify(
				`Your Port of Entry "${castedFormValues.port_of_entry_name}" has been successfully saved.`,
				'success',
			);
		} catch (error: any) {
			toasterNotify(
				'There was an unexpected error while saving the Port of Entry.',
				'error',
				error,
			);
		} finally {
			setIsSubmitting(false);
		}
		setShowAllErrors(false);
	};

	const changedFormValue = (values: FormValues, formId: string) => {
		if (formId === 'port_of_entry_name') {
			values['port_of_entry_name'] = values['port_of_entry_name']?.toUpperCase().replace(name_pattern, '');
		}
		if (formId === 'port_of_entry_code') {
			values['port_of_entry_code'] = values['port_of_entry_code']
				?.toUpperCase()
				.replace(/\s/g, '')
				.replace(code_pattern, '');
		}
		setFormValues(values);
	};

	return (
        <BaseModal show={show} onCancel={onClose} isLoading={isLoading} isSubmitting={isSubmitting}>
            <BaseModal.Title>
                {portOfEntry ? `Edit ${portOfEntry?.port_of_entry_name}` || 'Edit Port of Entry' : 'Add Port of Entry'}
            </BaseModal.Title>
            <Forms
				onChange={(id, v) => changedFormValue(id, v)}
                values={formValues ? formValues : {}}
                validationErrors={validationErrors}
                showAllErrors={showAllErrors}
            >
                <Forms.Text id="port_of_entry_code"
							disabled={!codecanBeEdited}
							maxLength={CODE_MAX_LEN}>
                    <Forms.Heading>Code</Forms.Heading>
                </Forms.Text>
                <Forms.Text id="port_of_entry_name"
							disabled={!editPermission}
							maxLength={NAME_MAX_LEN}>
                    <Forms.Heading>Name</Forms.Heading>
                </Forms.Text>
                <Forms.SingleCheckbox id={'status'} value={true} >
                    <Forms.Heading>Active</Forms.Heading>
                </Forms.SingleCheckbox>
            </Forms>

            {editPermission && (
                <BaseModal.Submit
                    onClick={() => {
                        submitForm();
                    }}
                >
                    {portOfEntry ? 'Update' : 'Add'}
                </BaseModal.Submit>
            )}
        </BaseModal>
	);
};

export default SavePortOfEntryModal;
